import { Calculator, Sparkles, GraduationCap, Menu, Radar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BottomNav = ({ activeTab, onTabChange, onMenuClick }) => {
  const { t } = useLanguage();
  const isSignalActive = activeTab === 'premium';

  return (
    <nav data-testid="bottom-nav" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      height: '80px',
      pointerEvents: 'none',
    }}>
      {/* SVG Background with concave notch */}
      <svg
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100px',
          pointerEvents: 'auto',
        }}
        viewBox="0 0 400 100"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="navShadow">
            <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
            <feOffset dy="-2" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.12" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d="M0 100 V28 Q0 8, 20 8 L145 8 C155 8, 160 8, 166 16 Q178 38, 200 38 Q222 38, 234 16 C240 8, 245 8, 255 8 L380 8 Q400 8, 400 28 V100 Z"
          fill="#FFFFFF"
          filter="url(#navShadow)"
        />
      </svg>

      {/* Floating Signal Button */}
      <button
        onClick={() => onTabChange('premium')}
        data-testid="bottomnav-premium"
        style={{
          position: 'absolute',
          left: '50%',
          top: '-8px',
          transform: 'translateX(-50%)',
          width: '58px',
          height: '58px',
          borderRadius: '50%',
          background: isSignalActive
            ? 'linear-gradient(145deg, #6E1030, #8B1538)'
            : 'linear-gradient(145deg, #8B1538, #B83A55)',
          border: '4px solid #FFFFFF',
          boxShadow: isSignalActive
            ? '0 6px 20px rgba(139,21,56,0.5)'
            : '0 4px 14px rgba(139,21,56,0.25)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          zIndex: 10,
          pointerEvents: 'auto',
        }}
      >
        <Sparkles size={24} strokeWidth={2.2} />
      </button>

      {/* Signal label */}
      <span style={{
        position: 'absolute',
        left: '50%',
        bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        fontWeight: '700',
        color: '#8B1538',
        zIndex: 10,
        pointerEvents: 'none',
      }}>
        Signal
      </span>

      {/* Left side items */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '38%',
        height: '72px',
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        pointerEvents: 'auto',
      }}>
        <NavItem
          id="tools"
          icon={Calculator}
          label={t('bottomNav.tools')}
          isActive={activeTab === 'tools'}
          onClick={() => onTabChange('tools')}
        />
        <NavItem
          id="scanner"
          icon={Radar}
          label="Scanner"
          isActive={activeTab === 'scanner'}
          onClick={() => onTabChange('scanner')}
        />
      </div>

      {/* Right side items */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '38%',
        height: '72px',
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        pointerEvents: 'auto',
      }}>
        <NavItem
          id="education"
          icon={GraduationCap}
          label={t('menu.education')}
          isActive={activeTab === 'education'}
          onClick={() => onTabChange('education')}
        />
        <NavItem
          id="menu"
          icon={Menu}
          label={t('bottomNav.menu')}
          isActive={false}
          onClick={onMenuClick}
        />
      </div>
    </nav>
  );
};

const NavItem = ({ id, icon: Icon, label, isActive, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`bottomnav-${id}`}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
      padding: '4px 8px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: isActive ? '#8B1538' : '#94A3B8',
      transition: 'color 0.2s ease',
      minWidth: '48px',
    }}
  >
    <Icon size={21} strokeWidth={isActive ? 2.4 : 1.7} />
    <span style={{
      fontSize: '9px',
      fontWeight: isActive ? '700' : '500',
      lineHeight: 1,
    }}>
      {label}
    </span>
  </button>
);

export default BottomNav;
